import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { api } from '../../api';
import { teamLogo, fmtCap, CapMeter, Confetti } from './poolFx';
import useStatsMode, { ProfileSlot, StatsHint } from './useStatsMode';

// GM Season trades — a trade machine: the two teams face each other (logo, payroll),
// the deal sits in the middle, both rosters underneath as cards you tap.
// Rules (database): 1 to 3 players each side, same positions both ways, salary cap,
// 2 trades a month per team. CPU GMs answer in a few seconds (server/season.js: schedule,
// injuries, streak, lineup fit, personality). Friends accept or decline in Offers.
// "Games" = the games his NHL team has left — points pile up between your games, so every one counts for you.
const STYLE = {
  tough: ['Tough negotiator', 'Négociateur coriace'],
  fair: ['Fair', 'Juste'],
  easy: ['Easy to deal with', 'Facile en affaires'],
  stars: ['Loves stars', 'Fou des vedettes'],
  youth: ['Loves young players', 'Fou des jeunes'],
};

export default function TradesView({ pool, userId, lang = 'en', standings, onTraded }) {
  const fr = lang === 'fr';
  const [members, setMembers] = useState([]);
  const [picks, setPicks] = useState([]);
  const [trades, setTrades] = useState([]);
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({});      // player_id -> { trend, pts, games } (last 10)
  const [tab, setTab] = useState('new');     // new | offers | history
  const [partner, setPartner] = useState('');
  const [give, setGive] = useState([]);
  const [get, setGet] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);      // { ok, text }
  // the offer you just sent, shown in place of the deal until it's answered:
  // { id, partner, give, get, status: pending | accepted | declined | failed | cancelled, reason }
  const [verdict, setVerdict] = useState(null);
  const stats = useStatsMode();   // hold a roster card 1 s = that player's profile under his card
  const [values, setValues] = useState({});   // player_id -> what he's worth to the OTHER team
  const [preview, setPreview] = useState(null); // how the other GM feels about the deal on screen: { level, pct, reason }

  // the parent passes a new onTraded function on every render: keep it in a ref so it never restarts our effects
  const onTradedRef = useRef(onTraded);
  onTradedRef.current = onTraded;

  const load = useCallback(async () => {
    const [{ data: m }, { data: p }, { data: t }] = await Promise.all([
      supabase.from('pool_members').select('user_id, profiles(username, is_cpu)').eq('pool_id', pool.id),
      supabase.from('pool_picks').select('*').eq('pool_id', pool.id),
      supabase.from('pool_trades').select('*').eq('pool_id', pool.id).order('created_at', { ascending: false }).limit(60),
    ]);
    // keep the same objects when nothing changed, so the 2-second check while waiting doesn't redraw the screen
    const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
    setMembers((prev) => (same(prev, m || []) ? prev : (m || [])));
    setPicks((prev) => (same(prev, p || []) ? prev : (p || [])));
    setTrades((prev) => (same(prev, t || []) ? prev : (t || [])));
  }, [pool.id]);

  useEffect(() => { load(); }, [load]);
  // nights per team: the server builds them from the whole season's schedule — ask again until it has them
  useEffect(() => {
    let alive = true, tries = 0, timer;
    const ask = () => api.fetchTradeInfo(pool.id).then((d) => {
      if (!alive) return;
      const ok = d && d.complete !== false && Object.keys(d.nights?.[userId] || {}).length > 0;
      if (ok || tries >= 6) setInfo(d);
      if (!ok && tries++ < 6) timer = setTimeout(ask, 5000);
    }).catch(() => { if (alive && tries++ < 6) timer = setTimeout(ask, 5000); });
    ask();
    return () => { alive = false; clearTimeout(timer); };
  }, [pool.id, userId]);
  useEffect(() => {
    const ch = supabase.channel(`pool-trades-${pool.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pool_trades', filter: `pool_id=eq.${pool.id}` }, (ev) => {
        load();
        if (ev?.new?.status === 'accepted') onTradedRef.current?.();   // rosters changed: refresh the standings
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [pool.id, load]);
  // the answer to the offer you just sent (CPU GMs answer in a few seconds): watch it come in
  useEffect(() => {
    if (!verdict || verdict.status !== 'pending') return undefined;
    const t = trades.find((x) => x.id === verdict.id);
    if (t && t.status !== 'pending') { setVerdict((v) => (v ? { ...v, status: t.status, reason: t.reason } : v)); if (t.status === 'accepted') onTradedRef.current?.(); return undefined; }
    const id = setInterval(load, 2000);   // in case the live update doesn't come through
    return () => clearInterval(id);
  }, [verdict, trades, load]);
  // last-10 form for the two teams on screen
  useEffect(() => {
    let alive = true;
    api.fetchTradeForm(pool.id, [userId, partner].filter(Boolean)).then((f) => { if (alive && f && !f.error) setForm((prev) => ({ ...prev, ...f })); }).catch(() => {});
    return () => { alive = false; };
  }, [pool.id, userId, partner]);

  // what each player is worth to the team you're dealing with
  useEffect(() => {
    setValues({});
    if (!partner) return undefined;
    let alive = true;
    api.fetchTradeValues(pool.id, partner, userId).then((v) => { if (alive && v && !v.error) setValues(v); }).catch(() => {});
    return () => { alive = false; };
  }, [pool.id, partner, userId, picks]);
  // interest meter: ask how that GM feels about the deal, a moment after you stop picking (CPU GMs only)
  const partnerIsCpu = !!members.find((m) => m.user_id === partner)?.profiles?.is_cpu;
  const giveKey = give.join(','), getKey = get.join(',');
  useEffect(() => {
    if (!partner || !partnerIsCpu || !giveKey || !getKey) { setPreview(null); return undefined; }
    let alive = true;
    const id = setTimeout(() => {
      api.fetchTradePreview(pool.id, partner, giveKey.split(','), getKey.split(',')).then((p) => { if (alive && p && !p.error) setPreview(p); }).catch(() => {});
    }, 350);
    return () => { alive = false; clearTimeout(id); };
  }, [pool.id, partner, partnerIsCpu, giveKey, getKey]);

  const teamOf = useMemo(() => Object.fromEntries(picks.filter((p) => p.position === 'T').map((p) => [p.user_id, p])), [picks]);
  const member = (uid) => members.find((m) => m.user_id === uid);
  const nameOf = (uid) => member(uid)?.profiles?.username || '—';
  const isCpu = (uid) => !!member(uid)?.profiles?.is_cpu;
  const byId = useMemo(() => Object.fromEntries(picks.map((p) => [p.player_id, p])), [picks]);
  // season stats + projection come with the standings (same numbers as the Players list)
  const extra = useMemo(() => Object.fromEntries((standings?.standings || []).flatMap((r) => (r.roster || []).map((p) => [p.player_id, p]))), [standings]);
  const rosterOf = (uid, pos) => picks.filter((p) => p.user_id === uid && p.position === pos)
    .sort((a, b) => (extra[b.player_id]?.proj || 0) - (extra[a.player_id]?.proj || 0));
  const nights = (team) => info?.nights?.[userId]?.[team];
  const payroll = (uid) => picks.filter((p) => p.user_id === uid).reduce((s, p) => s + Number(p.cap_hit || 0), 0);
  const capOf = (ids) => ids.reduce((s, id) => s + Number(byId[id]?.cap_hit || 0), 0);

  const now = new Date();
  const thisMonth = (d) => { const x = new Date(d); return x.getFullYear() === now.getFullYear() && x.getMonth() === now.getMonth(); };
  const tradesLeft = (uid) => Math.max(0, 2 - trades.filter((t) => t.status === 'accepted' && (t.from_user === uid || t.to_user === uid) && t.decided_at && thisMonth(t.decided_at)).length);

  const toggle = (list, set, id) => { setMsg(null); set(list.includes(id) ? list.filter((x) => x !== id) : list.length >= 3 ? list : [...list, id]); };
  const posKey = (ids) => ids.map((id) => byId[id]?.position).sort().join('');
  const ready = give.length > 0 && get.length > 0;
  const samePos = ready && posKey(give) === posKey(get);
  const hasCap = pool.cap_mode && pool.cap_amount != null;
  const myAfter = payroll(userId) - capOf(give) + capOf(get);
  const theirAfter = partner ? payroll(partner) - capOf(get) + capOf(give) : 0;
  const capBad = (after, raised) => hasCap && after > pool.cap_amount && (pool.cap_mode === 'hard' || raised);
  const capOk = !capBad(myAfter, capOf(get) > capOf(give)) && !capBad(theirAfter, capOf(give) > capOf(get));
  const leftOk = tradesLeft(userId) > 0 && (!partner || tradesLeft(partner) > 0);

  const send = async () => {
    setBusy(true); setMsg(null);
    const { data, error } = await supabase.rpc('pool_offer_trade', { p_user_id: userId, p_pool_id: pool.id, p_to_user: partner, p_give: give, p_get: get });
    setBusy(false);
    if (error || !data?.success) { setMsg({ ok: false, text: error?.message || data?.error }); return; }
    setVerdict({ id: data.trade_id, partner, give: [...give], get: [...get], status: 'pending', reason: null });
    setGive([]); setGet([]); load();
  };
  const answer = async (t, accept) => {
    const { data, error } = await supabase.rpc('pool_answer_trade', { p_user_id: userId, p_trade_id: t.id, p_accept: accept });
    if (error || !data?.success) setMsg({ ok: false, text: error?.message || data?.error });
    load(); if (accept) onTradedRef.current?.();
  };
  const cancel = async (t) => { await supabase.rpc('pool_cancel_trade', { p_user_id: userId, p_trade_id: t.id }); load(); };

  const logo = (abbrev, cls = 'tm-logo') => <img className={cls} alt="" src={teamLogo(abbrev)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />;
  const teamLabel = (uid) => teamOf[uid]?.player_name || nameOf(uid);
  const others = members.filter((m) => m.user_id !== userId && teamOf[m.user_id])
    .sort((a, b) => teamLabel(a.user_id).localeCompare(teamLabel(b.user_id)));
  const POS = [['F', fr ? 'Attaquants' : 'Forwards'], ['D', fr ? 'Défenseurs' : 'Defence'], ['G', fr ? 'Gardiens' : 'Goalies']];

  // NOTE: the pieces below are called as plain functions ({teamCard(...)}), never as <Tags />:
  // defined in here, a <Tag /> would be a new component on every render and re-mount (its entrance animation would replay).
  // one team's header card: logo, name, who runs it, payroll (as it would be after the deal)
  const TeamCard = ({ uid, after }) => (
    <div className={`tm-team ${uid === userId ? 'me' : ''}`}>
      {logo(teamOf[uid]?.team, 'tm-team-logo')}
      <div className="tm-team-main">
        <span className="tm-team-name">{teamLabel(uid)}</span>
        <span className="tm-team-who">
          {uid === userId ? (fr ? 'Toi' : 'You') : isCpu(uid) ? `CPU · ${(STYLE[info?.styles?.[uid]] || ['', ''])[fr ? 1 : 0]}` : nameOf(uid)}
          <i>·</i>{fr ? `${tradesLeft(uid)}/2 échanges ce mois-ci` : `${tradesLeft(uid)}/2 trades this month`}
        </span>
        {hasCap && <CapMeter payroll={ready ? after : payroll(uid)} cap={pool.cap_amount} label={ready ? (fr ? 'Masse après' : 'Payroll after') : (fr ? 'Masse' : 'Payroll')} />}
      </div>
    </div>
  );

  // a roster card you tap to put the player in the deal
  const PlayerCard = ({ p, sel, setSel }) => {
    const on = sel.includes(p.player_id);
    const x = extra[p.player_id] || {};
    const st = x.stats;
    const f = form[p.player_id];
    return (
      <>
      <button type="button" data-flip className={`tm-player sm-card ${on ? 'on' : ''} ${stats.cls(p.player_id)}`} {...stats.bind(p.player_id)}
        onClick={stats.click(p.player_id, () => toggle(sel, setSel, p.player_id))}>
        {logo(p.team)}
        <span className="tm-player-main">
          <span className="tm-player-name">{p.player_name}
            {f?.trend === 'hot' && <i className="tm-form hot" title={fr ? `En feu : ${f.pts} pts à ses ${f.games} derniers matchs` : `Hot: ${f.pts} pts in his last ${f.games} games`}>▲</i>}
            {f?.trend === 'cold' && <i className="tm-form cold" title={fr ? `Froid : ${f.pts} pts à ses ${f.games} derniers matchs` : `Cold: ${f.pts} pts in his last ${f.games} games`}>▼</i>}
          </span>
          <small>
            <b className="dr-slot-pos">{p.position}</b>{p.team} · {fmtCap(Number(p.cap_hit || 0))}
            {st && <span className="tm-stats">{p.position === 'G' ? `${st.gp ?? 0} ${fr ? 'PJ' : 'GP'} · ${st.w ?? 0} ${fr ? 'V' : 'W'}` : `${st.gp ?? 0} ${fr ? 'PJ' : 'GP'} · ${st.g ?? 0} ${fr ? 'B' : 'G'} · ${st.a ?? 0} A`}</span>}
          </small>
        </span>
        <span className="tm-num tm-proj"><b>{x.proj ?? '—'}</b><small>{fr ? 'prév' : 'proj'}</small></span>
        <span className="tm-num tm-nights" title={fr ? 'Matchs qu’il lui reste cette saison (chacun compte pour toi)' : 'Games he has left this season (every one counts for you)'}><b>{nights(p.team) ?? '—'}</b><small>{fr ? 'matchs' : 'games'}</small></span>
        <span className="tm-num tm-value" title={fr ? `Ce qu’il vaut pour ${teamLabel(partner)} (matchs restants, blessures, forme)` : `What he’s worth to ${teamLabel(partner)} (games left, injuries, form)`}><b>{values[p.player_id] ?? '—'}</b><small>{teamOf[partner]?.team || (fr ? 'eux' : 'them')}</small></span>
      </button>
      <ProfileSlot stats={stats} id={p.player_id} pick={p} lang={lang} compact poolPoints={x.points ?? null} />
      </>
    );
  };

  const Roster = ({ uid, sel, setSel, title }) => (
    <div className="tm-roster">
      <span className="tm-roster-title">{title} <small>{sel.length}/3</small></span>
      {POS.map(([pos, label]) => rosterOf(uid, pos).length > 0 && (
        <div key={pos} className="tm-group">
          <span className="tm-group-title" data-flip>{label}</span>
          {rosterOf(uid, pos).map((p) => <React.Fragment key={p.player_id}>{PlayerCard({ p, sel, setSel })}</React.Fragment>)}
        </div>
      ))}
    </div>
  );

  // a player inside the deal box / an offer
  const DealPlayer = ({ id, onRemove }) => {
    const p = byId[id];
    if (!p) return <span className="tm-deal-player">—</span>;
    return (
      <span className="tm-deal-player">
        {logo(p.team)}
        <span className="tm-deal-name">{p.player_name}<small><b className="dr-slot-pos">{p.position}</b>{fmtCap(Number(p.cap_hit || 0))} · {nights(p.team) ?? '—'} {fr ? 'matchs' : 'games'}</small></span>
        {onRemove && <button type="button" className="tm-x" onClick={onRemove} aria-label={fr ? 'Retirer' : 'Remove'}>×</button>}
      </span>
    );
  };
  const Check = ({ ok, children }) => <span className={`tm-check ${ok ? 'ok' : 'bad'}`}><i>{ok ? '✓' : '✕'}</i>{children}</span>;

  // an offer row (incoming / outgoing / history): team, what you give → what you get
  const Offer = ({ t, children }) => {
    const mineOut = t.from_user === userId;
    const other = mineOut ? t.to_user : t.from_user;
    return (
      <div className="tm-offer">
        <div className="tm-offer-head">
          {logo(teamOf[other]?.team, 'tm-offer-logo')}
          <span className="tm-offer-team">{teamLabel(other)}<small>{isCpu(other) ? 'CPU' : nameOf(other)} · {new Date(t.created_at).toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric' })}</small></span>
          <span className="tm-offer-side">{children}</span>
        </div>
        <div className="tm-offer-deal">
          <div className="tm-deal-col"><span className="tm-deal-label">{fr ? 'Tu donnes' : 'You give'}</span>{(mineOut ? t.give_ids : t.get_ids).map((id) => <React.Fragment key={id}>{DealPlayer({ id })}</React.Fragment>)}</div>
          <span className="tm-arrows" aria-hidden="true"><i>→</i><i>←</i></span>
          <div className="tm-deal-col"><span className="tm-deal-label">{fr ? 'Tu reçois' : 'You get'}</span>{(mineOut ? t.get_ids : t.give_ids).map((id) => <React.Fragment key={id}>{DealPlayer({ id })}</React.Fragment>)}</div>
        </div>
        {t.reason && <div className="tm-reason">“{t.reason}”</div>}
      </div>
    );
  };

  const incoming = trades.filter((t) => t.status === 'pending' && t.to_user === userId);
  const outgoing = trades.filter((t) => t.status === 'pending' && t.from_user === userId);
  const history = trades.filter((t) => t.status !== 'pending' && (t.from_user === userId || t.to_user === userId)).slice(0, 20);
  const STATUS = { accepted: fr ? 'Accepté' : 'Accepted', declined: fr ? 'Refusé' : 'Declined', cancelled: fr ? 'Annulé' : 'Cancelled', failed: fr ? 'Impossible' : 'Failed' };

  return (
    <div className="tm">
      <div className="pl-standings-tabs" role="tablist">
        {[['new', fr ? 'Nouvel échange' : 'New trade'], ['offers', `${fr ? 'Offres' : 'Offers'}${incoming.length + outgoing.length ? ` (${incoming.length + outgoing.length})` : ''}`], ['history', fr ? 'Historique' : 'History']].map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>

      {tab === 'new' && (
        <>
          {/* your team on the left · the other team on the right, picked from its dropdown */}
          <div className="tm-teams">
            <div className="tm-team-slot">
              <div className="tm-select tm-select-static">{fr ? 'Ton équipe' : 'Your team'}</div>
              {TeamCard({ uid: userId, after: myAfter })}
            </div>
            <div className="tm-team-slot">
              <select className="tm-select" value={partner} onChange={(e) => { setPartner(e.target.value); setGet([]); setMsg(null); }}>
                <option value="">{fr ? 'Choisis une équipe…' : 'Pick a team…'}</option>
                {others.map((m) => (
                  <option key={m.user_id} value={m.user_id}>{teamLabel(m.user_id)} · {m.profiles?.is_cpu ? 'CPU' : m.profiles?.username}</option>
                ))}
              </select>
              {partner ? TeamCard({ uid: partner, after: theirAfter }) : <div className="tm-team tm-team-empty">{fr ? 'Avec qui veux-tu échanger?' : 'Who do you want to trade with?'}</div>}
            </div>
          </div>

          {partner && (
            <>
              {verdict && verdict.partner === partner && (
                <div className={`tm-deal tm-verdict ${verdict.status}`}>
                  {verdict.status === 'accepted' && <Confetti pieces={16} durationMs={1600} />}
                  <span className="tm-deal-title">{fr ? 'Ton offre' : 'Your offer'}</span>
                  <div className="tm-deal-body">
                    <div className="tm-deal-col">
                      <span className="tm-deal-label">{verdict.status === 'accepted' ? (fr ? 'Tu as donné' : 'You gave') : (fr ? 'Tu donnes' : 'You give')}</span>
                      {verdict.give.map((id) => <React.Fragment key={id}>{DealPlayer({ id })}</React.Fragment>)}
                    </div>
                    <span className="tm-arrows" aria-hidden="true"><i>→</i><i>←</i></span>
                    <div className="tm-deal-col">
                      <span className="tm-deal-label">{verdict.status === 'accepted' ? (fr ? 'Tu as reçu' : 'You got') : (fr ? 'Tu reçois' : 'You get')}</span>
                      {verdict.get.map((id) => <React.Fragment key={id}>{DealPlayer({ id })}</React.Fragment>)}
                    </div>
                  </div>
                  <div className="tm-verdict-foot">
                    {verdict.status === 'pending' && (
                      <span className="tm-thinking">
                        {logo(teamOf[partner]?.team, 'tm-thinking-logo')}
                        {isCpu(partner)
                          ? (fr ? `${teamLabel(partner)} réfléchit` : `${teamLabel(partner)} is thinking`)
                          : (fr ? `En attente de ${nameOf(partner)}` : `Waiting for ${nameOf(partner)}`)}
                        <span className="tm-dots"><i /><i /><i /></span>
                      </span>
                    )}
                    {verdict.status === 'accepted' && <span className="tm-stamp ok">{fr ? 'Échange accepté' : 'Trade accepted'}</span>}
                    {['declined', 'failed'].includes(verdict.status) && <span className="tm-stamp no">{verdict.status === 'failed' ? (fr ? 'Échange impossible' : 'Trade failed') : (fr ? 'Échange refusé' : 'Trade declined')}</span>}
                    {verdict.status !== 'pending' && verdict.reason && <span className="tm-verdict-reason">“{verdict.reason}”</span>}
                    <span className="tm-verdict-actions">
                      {verdict.status === 'pending' && !isCpu(partner) && <button type="button" className="pool-leave-btn" onClick={() => setVerdict(null)}>{fr ? 'Fermer' : 'Close'}</button>}
                      {['declined', 'failed'].includes(verdict.status) && (
                        <button type="button" className="pool-start-btn" onClick={() => { setGive(verdict.give.filter((id) => byId[id]?.user_id === userId)); setGet(verdict.get.filter((id) => byId[id]?.user_id === partner)); setVerdict(null); }}>
                          {fr ? 'Modifier l’offre' : 'Change the offer'}
                        </button>
                      )}
                      {verdict.status !== 'pending' && <button type="button" className="pool-leave-btn" onClick={() => setVerdict(null)}>{verdict.status === 'accepted' ? (fr ? 'Super!' : 'Nice!') : (fr ? 'Fermer' : 'Close')}</button>}
                    </span>
                  </div>
                </div>
              )}

              <div className="tm-deal" style={verdict && verdict.partner === partner ? { display: 'none' } : undefined}>
                <span className="tm-deal-title">{fr ? 'L’échange' : 'The deal'}</span>
                <div className="tm-deal-body">
                  <div className="tm-deal-col">
                    <span className="tm-deal-label">{fr ? 'Tu donnes' : 'You give'}</span>
                    {give.length ? give.map((id) => <React.Fragment key={id}>{DealPlayer({ id, onRemove: () => toggle(give, setGive, id) })}</React.Fragment>)
                      : <span className="tm-deal-empty">{fr ? 'Choisis dans ton équipe' : 'Pick from your roster'}</span>}
                  </div>
                  <span className="tm-arrows" aria-hidden="true"><i>→</i><i>←</i></span>
                  <div className="tm-deal-col">
                    <span className="tm-deal-label">{fr ? 'Tu reçois' : 'You get'}</span>
                    {get.length ? get.map((id) => <React.Fragment key={id}>{DealPlayer({ id, onRemove: () => toggle(get, setGet, id) })}</React.Fragment>)
                      : <span className="tm-deal-empty">{fr ? 'Choisis dans leur équipe' : 'Pick from their roster'}</span>}
                  </div>
                </div>
                {ready && partnerIsCpu && (
                  <div className={`tm-meter ${preview?.level || 'wait'}`}>
                    <span className="tm-meter-head">
                      <span className="tm-deal-label">{fr ? 'Leur intérêt' : 'Their interest'}</span>
                      <b>{preview ? ({ no: fr ? 'Aucune chance' : 'No way', low: fr ? 'Pas assez' : 'Not enough', close: fr ? 'Presque' : 'Close', yes: fr ? 'Ils accepteraient' : 'They’d accept' })[preview.level] : '…'}</b>
                    </span>
                    <span className="tm-meter-bar"><i style={{ width: `${preview?.pct ?? 0}%` }} /></span>
                    {preview?.reason && <span className="tm-meter-why">“{preview.reason}”</span>}
                  </div>
                )}
                <div className="tm-deal-foot">
                  {ready && Check({ ok: samePos, children: fr ? 'Mêmes positions' : 'Same positions' })}
                  {ready && hasCap && Check({ ok: capOk, children: fr ? 'Plafond salarial' : 'Salary cap' })}
                  {Check({ ok: leftOk, children: fr ? 'Échanges ce mois-ci' : 'Trades this month' })}
                  <button type="button" className="pool-start-btn tm-send" disabled={busy || !samePos || !capOk || !leftOk} onClick={send}>
                    {busy ? '…' : (fr ? 'Envoyer l’offre' : 'Send offer')}
                  </button>
                </div>
                {msg && <div className={msg.ok ? 'tm-msg ok' : 'pool-form-error'}>{msg.text}</div>}
              </div>

              <StatsHint stats={stats} lang={lang} />
              <div data-flip-root className={`tm-rosters ${stats.statsMode ? 'stats-mode' : ''}`}>
                {Roster({ uid: userId, sel: give, setSel: setGive, title: fr ? 'Ton équipe' : 'Your roster' })}
                {Roster({ uid: partner, sel: get, setSel: setGet, title: teamLabel(partner) })}
              </div>
            </>
          )}
        </>
      )}

      {tab === 'offers' && (
        <div className="tm-offers">
          {incoming.length + outgoing.length === 0 && <div className="ss-empty">{fr ? 'Aucune offre en attente' : 'No offers waiting'}</div>}
          {incoming.map((t) => (
            <React.Fragment key={t.id}>{Offer({ t, children: (
              <>
                <button type="button" className="pool-start-btn" onClick={() => answer(t, true)}>{fr ? 'Accepter' : 'Accept'}</button>
                <button type="button" className="pool-leave-btn" onClick={() => answer(t, false)}>{fr ? 'Refuser' : 'Decline'}</button>
              </>
            ) })}</React.Fragment>
          ))}
          {outgoing.map((t) => (
            <React.Fragment key={t.id}>{Offer({ t, children: (
              <>
                <span className="tm-status">{fr ? 'En attente' : 'Waiting'}</span>
                <button type="button" className="pool-leave-btn" onClick={() => cancel(t)}>{fr ? 'Annuler' : 'Cancel'}</button>
              </>
            ) })}</React.Fragment>
          ))}
          {msg && !msg.ok && <div className="pool-form-error">{msg.text}</div>}
        </div>
      )}

      {tab === 'history' && (
        <div className="tm-offers">
          {history.length === 0 && <div className="ss-empty">{fr ? 'Aucun échange encore' : 'No trades yet'}</div>}
          {history.map((t) => <React.Fragment key={t.id}>{Offer({ t, children: <span className={`tm-status ${t.status}`}>{STATUS[t.status]}</span> })}</React.Fragment>)}
        </div>
      )}
    </div>
  );
}
